import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { 
  Users, 
  Calendar, 
  ArrowRight,
  CheckCircle,
  Globe,
  Sparkles,
  Target,
  TrendingUp,
  MessageSquare,
  Briefcase,
  HeartHandshake,
  Clock,
  Award,
  Shield,
  Star,
  Zap,
  GraduationCap,
  Play
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

// 数字递增动画 Hook
const useCountUp = (end: number, duration: number = 2000, startOnView: boolean = true) => {
  const [count, setCount] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!startOnView) {
      animateCount()
      return
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true)
          animateCount()
        }
      },
      { threshold: 0.3 }
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [end, duration, hasStarted, startOnView])

  const animateCount = () => {
    const startTime = Date.now()
    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      // easeOutExpo
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)
      setCount(Math.floor(eased * end))
      if (progress < 1) requestAnimationFrame(animate)
    }
    animate()
  }

  return { count, ref }
}

// 滚动显示动画 Hook
const useReveal = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return { ref, isVisible }
}

const HomePage = () => {
  const { isAuthenticated, user } = useAuthStore()
  const [mounted, setMounted] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    setMounted(true)
  }, [])
  
  // 鼠标跟踪效果（用于Hero区域的动态光效）
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100
    })
  }, [])

  const features = [
    {
      title: '全球名师直连',
      desc: '来自哈佛、牛津等顶尖学府的导师团队',
      icon: Globe,
      color: 'text-blue-500',
      bg: 'bg-blue-50'
    },
    {
      title: 'AI 智能助学',
      desc: '个性化学习路径规划与实时反馈',
      icon: Sparkles,
      color: 'text-purple-500',
      bg: 'bg-purple-50'
    },
    {
      title: '全方位保障',
      desc: '从申请到入学的全程无忧服务',
      icon: ShieldCheck,
      color: 'text-green-500',
      bg: 'bg-green-50'
    }
  ]

  const pathways = [
    {
      title: '语言冲刺营',
      desc: '托福 / 雅思 8 周提升，AI 精准评测每日跟进',
      icon: MessageSquare,
      highlights: ['小班互动', '每日口语反馈', '全真模考']
    },
    {
      title: '背景提升实验室',
      desc: '科研项目 + 海外实习 + 志愿者打包辅导，构建差异化履历',
      icon: Briefcase,
      highlights: ['导师一对一', '线上实验指导', '成果发表']
    },
    {
      title: '名校申请管家',
      desc: '院校定位、文书润色、Mock Interview 全流程保驾护航',
      icon: Target,
      highlights: ['AI + 人工双重审核', '多轮面试演练', 'offer 追踪']
    }
  ]

  const milestones = [
    {
      step: '第 1 个月',
      title: '评估与规划',
      desc: '完成性格 / 专业匹配评估，生成个人成长蓝图',
      status: 'completed'
    },
    {
      step: '第 3 个月',
      title: '能力跃升',
      desc: '语言成绩稳定提升 + 完成至少 1 个项目实践',
      status: 'active'
    },
    {
      step: '第 6 个月',
      title: '申请冲刺',
      desc: '联动导师完成文书、面试、奖学金申请',
      status: 'pending'
    }
  ]

  const testimonials = [
    {
      name: 'Eva Liu',
      school: '哥伦比亚大学 MSE',
      content: 'AI 规划器每天提醒我需要完成的模块，导师每周复盘，让备考节奏稳得可怕。',
      result: '托福 96→109 / 拿到 4 个美研 Offer'
    },
    {
      name: 'Nathan Chen',
      school: '牛津大学 PPE',
      content: '平台的科研实验室项目直接让我与导师共推论文，面试也有全英文陪练。',
      result: '科研发表 + Half Scholarship'
    },
    {
      name: 'Sofia Zhang',
      school: 'UCL 建筑学',
      content: '作品集、文书、跨学科课程全部在这完成，AI 自动整理素材，省心！',
      result: '作品集评分提升 30%'
    }
  ]

  const partners = [
    { name: '哈佛大学', stat: '长期合作导师 38 位' },
    { name: '帝国理工', stat: '研究项目合作 12 个' },
    { name: '麦肯锡', stat: '职业体验营 6 期' },
    { name: '联合国志愿者', stat: '海外志愿名额 80+' }
  ]

  // 统计数据的动画
  const stat1 = useCountUp(2000, 2500)
  const stat2 = useCountUp(500, 2000)
  const stat3 = useCountUp(99, 1500)
  const stat4 = useCountUp(20, 1500)
  
  // 各区块的显示动画
  const featuresReveal = useReveal()
  const pathwaysReveal = useReveal()
  const timelineReveal = useReveal()
  const partnersReveal = useReveal()
  const testimonialsReveal = useReveal()

  return (
    <div className="min-h-screen overflow-hidden bg-[#FAFBFC]">
      {/* Hero Section - 高级版 */}
      <section 
        className="relative min-h-[100vh] flex items-center justify-center overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        {/* 高级动态背景 */}
        <div className="absolute inset-0 z-0">
          {/* 主渐变背景 */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900"></div>
          
          {/* 网格图案 */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          ></div>
          
          {/* 动态光晕 - 跟随鼠标 */}
          <div 
            className="absolute w-[800px] h-[800px] rounded-full blur-[150px] transition-all duration-1000 ease-out"
            style={{
              background: 'radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)',
              left: `${mousePosition.x}%`,
              top: `${mousePosition.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
          ></div>
          
          {/* 装饰性光球 */}
          <div className="absolute -top-[20%] -right-[5%] w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-[120px] animate-pulse-subtle"></div>
          <div className="absolute top-[60%] -left-[10%] w-[500px] h-[500px] bg-purple-500/15 rounded-full blur-[100px] animate-pulse-subtle delay-1000"></div>
          <div className="absolute top-[20%] left-[60%] w-[300px] h-[300px] bg-cyan-500/10 rounded-full blur-[80px] animate-float-slow"></div>
          
          {/* 动态形状装饰 */}
          <div className="absolute top-[15%] right-[15%] w-24 h-24 border border-white/10 rounded-2xl animate-float rotate-12"></div>
          <div className="absolute bottom-[25%] left-[10%] w-16 h-16 border border-white/10 rounded-full animate-float delay-500"></div>
          <div className="absolute top-[40%] right-[8%] w-3 h-3 bg-blue-400/60 rounded-full animate-pulse"></div>
          <div className="absolute bottom-[35%] left-[20%] w-2 h-2 bg-purple-400/60 rounded-full animate-pulse delay-300"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 pt-24 pb-12">
          <div className="max-w-6xl mx-auto text-center">
            <div className={`transition-all duration-1000 transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              {/* 信任徽章 */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 text-blue-200 text-sm font-medium border border-white/10 backdrop-blur-md">
                  <Sparkles className="w-4 h-4 mr-2 text-amber-400" />
                  AI 驱动的新一代留学教育平台
                </span>
                <span className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 text-xs font-bold border border-amber-500/20 backdrop-blur-sm">
                  <Award className="w-3.5 h-3.5 mr-1.5" />
                  2024 年度最佳教育平台
                </span>
              </div>
              
              {/* 主标题 - 更精致的渐变 */}
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold text-white mb-8 leading-[1.1] tracking-tight">
                开启您的
                <br className="sm:hidden" />
                <span className="relative inline-block mx-2">
                  <span className="text-gradient-hero">全球名校</span>
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                    <path d="M2 10C50 2 150 2 198 10" stroke="url(#underline-gradient)" strokeWidth="3" strokeLinecap="round"/>
                    <defs>
                      <linearGradient id="underline-gradient" x1="0" y1="0" x2="200" y2="0">
                        <stop offset="0%" stopColor="#60A5FA"/>
                        <stop offset="50%" stopColor="#A78BFA"/>
                        <stop offset="100%" stopColor="#F472B6"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
                之旅
              </h1>
              
              {/* 副标题 */}
              <p className="text-lg sm:text-xl md:text-2xl text-gray-300/90 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
                连接全球顶尖教育资源，利用 AI 技术为您量身定制留学方案
                <br className="hidden sm:block" />
                从语言培训到背景提升，我们陪伴您每一步
              </p>

              {/* CTA 按钮组 */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                <Link 
                  to="/courses"
                  className="btn-primary-new min-w-[200px] text-lg group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    开始探索
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
                  </span>
                </Link>
                <Link 
                  to="/teachers"
                  className="px-8 py-3.5 rounded-2xl bg-white/5 text-white font-semibold border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 backdrop-blur-md min-w-[200px] text-lg flex items-center justify-center gap-2.5 group"
                >
                  <Users className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  寻找导师
                </Link>
              </div>
              
              {/* 信任指标 */}
              <div className="flex flex-wrap items-center justify-center gap-6 mb-16 text-sm text-gray-400">
                <span className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-400" />
                  安全支付保障
                </span>
                <span className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-400" />
                  4.9/5 用户评分
                </span>
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-400" />
                  48小时极速响应
                </span>
              </div>

              {/* 数据展示 - 带动画 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 border-t border-white/10 pt-12">
                <div ref={stat1.ref} className="text-center group">
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2 stat-number">
                    {stat1.count.toLocaleString()}+
                  </div>
                  <div className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">名校 Offer</div>
                </div>
                <div ref={stat2.ref} className="text-center group">
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2 stat-number">
                    {stat2.count}+
                  </div>
                  <div className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">认证导师</div>
                </div>
                <div ref={stat3.ref} className="text-center group">
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2 stat-number">
                    {stat3.count}%
                  </div>
                  <div className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">好评率</div>
                </div>
                <div ref={stat4.ref} className="text-center group">
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2 stat-number">
                    {stat4.count}+
                  </div>
                  <div className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">覆盖国家</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FAFBFC] to-transparent z-10"></div>
      </section>

      {/* 特性展示区 - 高级版 */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-50 rounded-full blur-[100px] opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-50 rounded-full blur-[100px] opacity-50"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* 区块标题 */}
          <div 
            ref={featuresReveal.ref}
            className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${featuresReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-4">
              <Zap className="w-4 h-4 mr-2" />
              为什么选择我们
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              全方位的<span className="text-gradient-primary">教育支持</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              我们整合全球顶尖教育资源，结合 AI 技术，为您提供个性化的留学规划服务
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i}
                className={`group p-8 rounded-3xl glass-card-premium hover-card-subtle cursor-default transition-all duration-500 ${featuresReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 100 + 200}ms` }}
              >
                <div className={`w-16 h-16 rounded-2xl ${feature.bg} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                  <feature.icon className={`w-8 h-8 ${feature.color}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.desc}
                </p>
                <div className="mt-6 flex items-center text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-[-10px] group-hover:translate-x-0">
                  了解更多 <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 学习路径 - 高级版 */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        {/* 装饰元素 */}
        <div className="absolute top-20 left-10 w-20 h-20 border-2 border-blue-100 rounded-2xl rotate-12 opacity-50"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 border-2 border-purple-100 rounded-full opacity-50"></div>
        
        <div className="container mx-auto px-4">
          <div 
            ref={pathwaysReveal.ref}
            className={`max-w-4xl mx-auto text-center mb-16 transition-all duration-700 ${pathwaysReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-purple-50 text-purple-600 text-sm font-semibold mb-4">
              <GraduationCap className="w-4 h-4 mr-2" />
              Learning Journey
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              多轨并行，找到<span className="text-gradient-premium">最适合你</span>的路线
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              我们将语言、背景、申请节点拆解成可执行的 Sprint，每个阶段都有 AI 记录与导师陪跑
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pathways.map((pathway, index) => (
              <div 
                key={pathway.title} 
                className={`group relative p-8 rounded-3xl bg-white border border-gray-100 hover:border-blue-200 transition-all duration-500 hover-card-subtle ${pathwaysReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150 + 200}ms` }}
              >
                {/* 编号标签 */}
                <div className="absolute -top-3 -right-3 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                  0{index + 1}
                </div>
                
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                  <pathway.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{pathway.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{pathway.desc}</p>
                <ul className="space-y-3">
                  {pathway.highlights.map(item => (
                    <li key={item} className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                
                {/* 底部装饰线 */}
                <div className="absolute bottom-0 left-8 right-8 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 数据 + 时间线 - 高级版 */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div 
          ref={timelineReveal.ref}
          className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
        >
          <div className={`space-y-8 transition-all duration-700 ${timelineReveal.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div>
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-semibold mb-4">
                <TrendingUp className="w-4 h-4 mr-2" />
                平台可信数据
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">实时可见的<span className="text-gradient-primary">成长曲线</span></h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                AI 会同步你的学习轨迹、项目进度与面试准备情况，所有数据在 App 内一键查看。
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: '平均申请周期', value: '4.6 个月', icon: Calendar, color: 'blue' },
                { label: 'AI 指导匹配度', value: '92%', icon: Target, color: 'purple' },
                { label: '科研/实习转化率', value: '78%', icon: TrendingUp, color: 'green' },
                { label: '导师响应时间', value: '<6 小时', icon: Clock, color: 'amber' },
              ].map((stat, index) => (
                <div 
                  key={stat.label} 
                  className={`p-5 rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white hover:shadow-lg transition-all duration-300 group ${timelineReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                  style={{ transitionDelay: `${index * 100 + 300}ms` }}
                >
                  <div className={`w-10 h-10 rounded-xl bg-${stat.color}-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* 时间线卡片 */}
          <div className={`relative transition-all duration-700 delay-300 ${timelineReveal.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-[2rem] blur-2xl opacity-20"></div>
            <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white rounded-3xl p-8 md:p-10 shadow-2xl border border-white/10">
              <div className="flex items-center justify-between mb-8">
                <p className="text-sm uppercase tracking-[0.35em] text-gray-400 font-semibold">Project Timeline</p>
                <span className="badge-verified">
                  <Shield className="w-3 h-3" />
                  实时追踪
                </span>
              </div>
              <div className="space-y-6">
                {milestones.map((step, index) => (
                  <div key={step.step} className="flex items-start gap-5 group">
                    <div className="relative">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-sm font-bold transition-all duration-300 group-hover:scale-110
                        ${step.status === 'completed' ? 'bg-gradient-to-br from-green-400/20 to-emerald-500/20 text-green-300 border border-green-500/30' :
                          step.status === 'active' ? 'bg-gradient-to-br from-blue-400/20 to-indigo-500/20 text-blue-300 border border-blue-500/30 animate-pulse' : 
                          'bg-gray-800 text-gray-500 border border-gray-700'}`}>
                        {step.status === 'completed' ? <CheckCircle className="w-6 h-6" /> : step.step.replace('第 ', '').replace(' 个月', 'M')}
                      </div>
                      {index < milestones.length - 1 && (
                        <div className={`absolute left-1/2 top-full w-0.5 h-6 -translate-x-1/2 ${step.status === 'completed' ? 'bg-gradient-to-b from-green-500/50 to-transparent' : 'bg-gray-700'}`}></div>
                      )}
                    </div>
                    <div className="pt-1 flex-1">
                      <h4 className="text-lg font-bold text-white mb-1 group-hover:text-blue-300 transition-colors">{step.title}</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 合作伙伴 - 高级版 */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #64748B 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}
        ></div>
        
        <div 
          ref={partnersReveal.ref}
          className="container mx-auto px-4 text-center relative z-10"
        >
          <div className={`transition-all duration-700 ${partnersReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-4">
              <Globe className="w-4 h-4 mr-2" />
              Global Network
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              与<span className="text-gradient-primary">世界级机构</span>建立深度合作
            </h2>
            <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
              我们与全球顶尖院校和企业建立了紧密的合作关系，为学员提供独家资源
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {partners.map((partner, index) => (
              <div 
                key={partner.name} 
                className={`group p-6 md:p-8 bg-white rounded-2xl border border-gray-100 hover:border-blue-200 transition-all duration-500 hover-card-subtle ${partnersReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                {/* Logo占位 - 可替换为实际logo */}
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl font-bold text-gray-400 group-hover:text-blue-500 transition-colors">{partner.name[0]}</span>
                </div>
                <p className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{partner.name}</p>
                <p className="text-sm text-gray-500">{partner.stat}</p>
              </div>
            ))}
          </div>
          
          {/* 信任标识 */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-gray-400">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">官方认证合作</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              <span className="text-sm font-medium">独家教育资源</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              <span className="text-sm font-medium">顶尖导师团队</span>
            </div>
          </div>
        </div>
      </section>

      {/* 学员故事 */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amber-50 text-amber-600 text-sm font-semibold mb-4">
              <Star className="w-4 h-4 mr-2" />
              Success Stories
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">他们的<span className="text-gradient-gold">Offer</span>故事</h2>
            <p className="text-lg text-gray-600">来自不同学科、不同背景的学生，在这里走向梦想的校园与舞台</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={testimonial.name} className="group relative p-8 rounded-3xl bg-gradient-to-br from-white to-gray-50 border border-gray-100 hover:border-amber-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-500">
                <p className="text-gray-800 italic mb-4 leading-relaxed">“{testimonial.content}”</p>
                <div className="text-sm text-gray-500 mb-1">{testimonial.result}</div>
                <div className="font-semibold text-gray-900">{testimonial.name}</div>
                <div className="text-sm text-gray-500">{testimonial.school}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - 高级版 */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
        <div className="absolute -top-[30%] left-[20%] w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px]"></div>
        <div className="absolute -bottom-[20%] right-[10%] w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[80px]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <span className="inline-flex items-center px-5 py-2 rounded-full bg-white/5 text-blue-300 text-sm font-semibold mb-8 border border-white/10 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 mr-2 text-amber-400" />
              Ready to Start Your Journey
            </span>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">与 AI 教学团队一起<br /><span className="text-gradient-hero">设计你的下一段旅程</span></h2>
            <p className="text-xl text-gray-300/90 mb-12 max-w-3xl mx-auto leading-relaxed">
              提交成绩单、兴趣、职业目标，我们将在 <span className="text-white font-semibold">48 小时内</span>产出一份精准的成长路线图，并<span className="text-amber-300 font-semibold">免费</span>安排一次名师面谈
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to={isAuthenticated ? '/student/ai' : '/register'} className="btn-gold min-w-[220px] text-lg group">
                <Sparkles className="w-5 h-5" />
                立即领取规划方案
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="mailto:admission@philiapedia.com"
                className="px-8 py-3.5 rounded-2xl border-2 border-white/20 text-white font-semibold hover:bg-white/10 hover:border-white/30 transition-all duration-300 flex items-center gap-2.5 backdrop-blur-sm"
              >
                <HeartHandshake className="w-5 h-5" />
                与顾问通话
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-400">
              <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-400" />不满意全额退款</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-400" />24/7 在线支持</span>
              <span className="flex items-center gap-2"><Users className="w-4 h-4 text-purple-400" />50,000+ 学员信赖</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

// 补充缺失的图标组件
const ShieldCheck = (props: any) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
)

export default HomePage
